// View as member is read-only (CLAUDE.md, Security; docs/data-model.md 5.1). While an owner or
// admin views as a member, hub.whoami() says viewing_as, the screens show that member's rows, and
// nothing can be changed: every write the database or a function would refuse anyway
// (writer_person_id() is null, the functions answer 403 viewing_as) is refused on the screen
// first, with this 1 line, so no control looks as if it worked.
//
// For a screen:
//   const viewingAs = useViewingAs()
//   <ReadOnlyNote />                                  the line, only while viewing as
//   <button {...readOnlyControl(viewingAs)}>          disabled, with the line as its title
//   <input {...readOnlyField(viewingAs)}>             read-only
//   if (isViewingAsRefusal(error)) ...                a function's own viewing_as answer
//                                                     (a HubFunctionError from lib/functions.js)
//
// Starting and stopping (stage 5), for admin's person page and the bar:
//   const viewAs = useViewAs()
//   viewAs.canViewAs(person)          owner or admin at 2-step login; a member of staff only for an owner
//   await viewAs.start(person, { to, back })
//                                     hub.start_view_as, then whoami again, then `to` as them (the
//                                     Library by default); Stop viewing returns to `back`, their
//                                     admin page by default (Resources' See it as a member: Resources)
//   await viewAs.stop()               hub.stop_view_as, then whoami again, then back where it began
//   <ViewAsBar />                     the bar on every page while viewing as (src/app/App.jsx draws it)
// Each start and stop is audited by the database (view_as_member_start, view_as_member_stop). A view
// as ends by itself after 2 hours; the bar asks whoami again every few minutes and when the window
// comes back, so it goes as soon as the database has ended it.
import { useCallback, useEffect, useState } from 'react'
import { useMember } from '../app/member.jsx'
import { supabase } from './supabase.js'
import { navigate } from './router.jsx'
import { time } from './format.js'

// The same words the stage 3 functions answer with (_shared/member.ts)
export const VIEWING_AS_LINE = "You're viewing as a member, so nothing here can be changed."

// True while staff view as a member, from hub.whoami()
export function useViewingAs() {
  const member = useMember()
  return Boolean(member?.me?.who?.viewing_as)
}

// The line, in the shell's look, only while viewing as. role="note": it describes the page, it
// isn't an error.
export function ReadOnlyNote({ className = '' }) {
  const viewingAs = useViewingAs()
  if (!viewingAs) return null
  return <p className={`viewas-note ${className}`.trim()} role="note">{VIEWING_AS_LINE}</p>
}

// A button or a link that writes: disabled while viewing as
export function readOnlyControl(viewingAs) {
  return viewingAs ? { disabled: true, 'aria-disabled': 'true', title: VIEWING_AS_LINE } : {}
}

// A field: still shows the member's value, but can't be typed into
export function readOnlyField(viewingAs) {
  return viewingAs ? { readOnly: true, 'aria-readonly': 'true' } : {}
}

// A function's refusal while viewing as: a HubFunctionError (lib/functions.js), or its JSON body
export function isViewingAsRefusal(e) {
  return e?.code === 'viewing_as' || e?.error === 'viewing_as' || e?.body?.error === 'viewing_as'
}

// ---- Starting, stopping and the bar (stage 5) ----------------------------------------------------

export const VIEW_AS_HOURS = 2
const STORE = 'hub-view-as'
const POLL_MS = 5 * 60 * 1000

// This tab's own note of who it's viewing as and since when, for the bar's end time and the way back.
// Only a convenience: the database decides whether a view as is open (private.view_as_target).
function remember(v) { try { sessionStorage.setItem(STORE, JSON.stringify(v)) } catch { /* private window */ } }
function recalled() { try { return JSON.parse(sessionStorage.getItem(STORE) || 'null') } catch { return null } }
function forget() { try { sessionStorage.removeItem(STORE) } catch { /* private window */ } }

// The words for start_view_as's and stop_view_as's refusals: the database's own sentences
// ('Only an owner can view as a member of staff', 'There''s no member to view as with that id')
function wordsOf(error) {
  const m = error?.message || ''
  if (/^[A-Z]/.test(m) && !/JWT|PGRST|relation|permission denied/i.test(m)) return /[.?]$/.test(m) ? m : `${m}.`
  if (error?.code === '42501') return 'Only an owner or admin can view as a member.'
  if (/fetch|network|load failed/i.test(m)) return "That didn't load. Check your connection and try again."
  return 'Something went wrong. Try again.'
}

export function useViewAs() {
  const { me, loadMe } = useMember()
  const [state, setState] = useState({ busy: false, error: '' })
  const ready = me?.status === 'ready'
  const viewingAs = ready && Boolean(me.who?.viewing_as)
  const role = ready && !viewingAs ? me.who?.staff_role || null : null
  const person = ready ? me.person : null

  const canViewAs = useCallback(target => {
    if (!target?.id || !(role === 'owner' || role === 'admin')) return false
    if (target.id === me.who?.person_id || target.anonymised_at || target.merged_into_id) return false
    return !target.role || role === 'owner'
  }, [role, me])

  const start = useCallback(async (target, { to = '/', back } = {}) => {
    if (state.busy || !target?.id) return false
    setState({ busy: true, error: '' })
    const { error } = await supabase.rpc('start_view_as', { p_person: target.id }).then(r => r, e => ({ error: e }))
    if (error) { setState({ busy: false, error: wordsOf(error) }); return false }
    // only an admin address is a way back
    const way = typeof back === 'string' && /^\/admin(\/|\?|$)/.test(back) ? back : `/admin/people/${target.id}`
    remember({ id: target.id, name: target.full_name || null, startedAt: Date.now(), back: way })
    await loadMe()
    setState({ busy: false, error: '' })
    navigate(to)
    return true
  }, [state.busy, loadMe])

  const stop = useCallback(async ({ to } = {}) => {
    if (state.busy) return false
    const stored = recalled()
    const target = person
    const back = to || (stored && target && stored.id === target.id && stored.back) || (target ? `/admin/people/${target.id}` : '/admin')
    setState({ busy: true, error: '' })
    const { error } = await supabase.rpc('stop_view_as').then(r => r, e => ({ error: e }))
    if (error) { setState({ busy: false, error: wordsOf(error) }); return false }
    forget()
    await loadMe()
    setState({ busy: false, error: '' })
    navigate(back)
    return true
  }, [state.busy, person, loadMe])

  const stored = viewingAs ? recalled() : null
  const endsAt = stored && person && stored.id === person.id && stored.startedAt ? stored.startedAt + VIEW_AS_HOURS * 3600 * 1000 : null

  return { viewingAs, person: viewingAs ? person : null, role, canViewAs, start, stop, busy: state.busy, error: state.error, endsAt }
}

// The bar on every page while staff view as a member. No board draws it; the words are in the stage 5
// batch for Adam.
export function ViewAsBar() {
  const { me, loadMe } = useMember()
  const { viewingAs, person, stop, busy, error, endsAt } = useViewAs()

  // the database ends a view as after 2 hours: ask again now and then, and when the window comes back
  useEffect(() => {
    if (!viewingAs) return undefined
    let alive = true
    const check = async () => {
      const { data } = await supabase.rpc('whoami').then(r => r, () => ({ data: null }))
      const who = data?.[0]
      if (alive && who && !who.viewing_as) { forget(); loadMe() }
    }
    const timer = setInterval(check, POLL_MS)
    const onFocus = () => { if (document.visibilityState !== 'hidden') check() }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => { alive = false; clearInterval(timer); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus) }
  }, [viewingAs, loadMe, me?.person?.id])

  if (!viewingAs || !person) return null
  const name = person.full_name || person.email
  return (
    <div className="va-bar" role="region" aria-label="Viewing as a member">
      <div className="va-bar-text">
        <span className="va-bar-line"><span className="va-bar-dot" aria-hidden="true" />Viewing as {name}. Nothing you do here changes anything.</span>
        <span className="va-bar-ends">{endsAt ? `It ends by itself at ${time(endsAt)}.` : `It ends by itself ${VIEW_AS_HOURS} hours after it started.`}</span>
        {error && <p className="va-bar-error" role="alert">{error}</p>}
      </div>
      <button type="button" className="va-bar-stop" onClick={() => stop()} disabled={busy} aria-busy={busy || undefined}>Stop viewing</button>
    </div>
  )
}

// Logging out while viewing as ends the view as first, so its stop is in the audit log
export async function stopViewingBeforeLogOut(me) {
  if (!me?.who?.viewing_as) return
  forget()
  await supabase.rpc('stop_view_as').then(() => {}, () => {})
}
